import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Location, isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import * as urlModule from 'url';

import { IAppState } from '@app/store';
import { Logger } from '@app/core/logging';
import { SetCatalogPageNumber } from '@app/+catalog/store';
import { SearchRouter, RouteState, UrlParser } from '@app/core/search/routing';

@Injectable()
export class SearchRoutingService implements SearchRouter {
  /**
   * Prevents having too much entries in the history and thus provides a workaround to the issue
   * with the security limitation of the browers in trying to write too much URLs in the history stack.
   * @see https://github.com/algolia/instantsearch.js/blob/v2.10.2/src/lib/routers/history.js#L62
   */
  private writeDelay = 400;
  private writeTimer: NodeJS.Timer;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private location: Location,
    private store: Store<IAppState>,
    private logger: Logger
  ) {}

  /**
   * Read the URL and returns a syncable UI search state.
   * @returns the equivalent to what is stored in the URL as an object.
   */
  read(): RouteState {
    const url = '/' + this.location.path(true);
    const urlParser = new UrlParser(url);
    const routeState = urlParser.getRouteState();

    this.logger.debug('[Search] router.read', { routeState });

    return { ...routeState };
  }

  /**
   * Push a search state into the URL.
   * @param routeState
   */
  write(routeState: RouteState): void {
    const page = parseInt(routeState.params.page, 10);
    const url = this.createURL(routeState);

    this.store.dispatch(new SetCatalogPageNumber({ pageNumber: page > 0 ? page : 1 }));

    this.clearWriteTimer();

    this.writeTimer = global.setTimeout(() => {
      this.logger.debug('[Search] router.write', url);
      this.location.replaceState(url);
      this.clearWriteTimer();
    }, this.writeDelay);
  }

  private clearWriteTimer(): void {
    if (this.writeTimer && isPlatformBrowser(this.platformId)) {
      global.clearTimeout(this.writeTimer);
      this.writeTimer = undefined;
    }
  }

  /**
   * This method removes the `onpopstate` event listener and cleans up the URL.
   */
  dispose(): void {
    this.logger.debug('[Search] router.dispose');
    this.clearWriteTimer();
  }

  /**
   * Set a callback on the `onpopstate` event of the history API of the current page.
   * This way, the URL sync can keep track of the changes.
   *
   * @param callback the callback that will receive the latest routeState.
   *                 It is called when the URL is updated.
   */
  onUpdate(callback: (routeState: RouteState) => {}): void {
    this.logger.debug('[Search] router.onUpdate');
  }

  /**
   * Create a complete URL from a given syncable UI state.
   *
   * @param routeState a syncable UI state
   * @returns the full URL for the provided syncable state
   */
  createURL(routeState: RouteState): string {
    // Current page is obviously not a catalog page.
    if (routeState.pathname.endsWith('.shtml')) {
      return routeState.pathname;
    }

    const pathSuffixes: string[] = [];

    if (routeState.params.page && routeState.params.page !== '1') {
      pathSuffixes.push(`p-${routeState.params.page}/`);
    }

    const pathname = routeState.pathname.concat(...pathSuffixes);
    const params = [];

    Object.keys(routeState.params).forEach(param => {
      const value = encodeURIComponent(routeState.params[param]);
      if (param !== 'page') {
        params.push(`${param}=${value}`);
      }
    });
    const url = this.combineUrl(routeState.originalUrl, pathname, params.join('_'));

    this.logger.debug('[Search] router.createURL', url);

    return url;
  }

  private combineUrl(url: string, pathname: string, hash: string): string {
    const source = urlModule.parse(url);

    source.pathname = pathname;
    source.hash = hash.length ? `#${hash}` : '';

    return urlModule.format(source);
  }
}
